import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Stack, Typography, Paper, Table, TableContainer, TableHead, TableBody,
  TableRow, TableCell, TextField, ToggleButton, ToggleButtonGroup,
  InputAdornment, Chip, Tooltip, IconButton, LinearProgress, Snackbar, Alert,
  CircularProgress, Collapse, Checkbox, Divider, Button, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import SearchIcon from '@mui/icons-material/Search';
import ListAltIcon from '@mui/icons-material/ListAlt';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import authService from '../services/auth';

const API = process.env.REACT_APP_API_URL;

// Deliberately no `limit`: every matching control must be reachable, so the
// annotator is never silently shown a truncated list. Matching covers id, title,
// family and statement text, and the id is also indexed without its leading zero
// so "AU-2" finds AU-02. Rows are plain DOM rather than MUI components, which is
// what keeps rendering all 1,014 at once fast enough.
const controlFilter = createFilterOptions({
  stringify: (c) => `${c.id} ${c.id.replace('-0', '-')} ${c.title} ${c.family_title} ${c.short}`,
});

const VERDICT_LABEL = { c: 'Correct', p: 'Partial', w: 'Wrong' };
const VERDICT_COLOR = { c: 'success', p: 'warning', w: 'error' };

const VENDOR_LABEL = {
  cisco_ftd: 'Cisco FTD',
  paloalto_panos: 'Palo Alto PAN-OS',
  juniper_srx: 'Juniper SRX',
  fortinet_fortigate: 'Fortinet FortiGate',
  huawei_usg: 'Huawei USG',
  checkpoint_security_gateway: 'Check Point',
};

const SEVERITY_COLOUR = {
  Critical: '#d32f2f',
  High: '#f57c00',
  Medium: '#fbc02d',
  Low: '#388e3c',
};

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
}

export default function Annotate() {
  const [items, setItems] = useState([]);
  const [answers, setAnswers] = useState({});
  const [annotator, setAnnotator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The JWT lives 8 hours but the queue takes days to work through, so a
  // token expiring mid-session is routine rather than exceptional. Tracked
  // separately from `error`: it is recoverable by signing in again, and the
  // raw "HTTP 401" that used to surface here told the annotator nothing.
  const [sessionExpired, setSessionExpired] = useState(false);
  const [toast, setToast] = useState(null);
  const [showRubric, setShowRubric] = useState(false);
  const [controls, setControls] = useState([]);
  const [showControlList, setShowControlList] = useState(false);
  const [controlSearch, setControlSearch] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [unsaved, setUnsaved] = useState(() => new Set());
  const [lastSaved, setLastSaved] = useState(null);
  const [copied, setCopied] = useState(false);

  const [statusFilter, setStatusFilter] = useState('all');   // all | todo | done
  const [vendorFilter, setVendorFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/annotation/queue`, { headers: authHeaders() });
        if (res.status === 401) {
          if (!cancelled) setSessionExpired(true);
          return;
        }
        if (!res.ok) throw new Error(`Could not load the queue (HTTP ${res.status})`);
        const data = await res.json();
        if (cancelled) return;
        setItems(data.items || []);
        setAnswers(data.answers || {});
        setAnnotator(data.annotator || null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // The catalogue backs both the Correct-control picker and the printed list.
  // A failure here is not fatal: the picker falls back to accepting free text.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/annotation/controls`, { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setControls(data.controls || []);
      } catch {
        /* picker degrades to free text */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const controlById = useMemo(
    () => new Map(controls.map((c) => [c.id, c])),
    [controls]
  );

  const listedControls = useMemo(() => {
    const q = controlSearch.trim().toLowerCase();
    if (!q) return controls;
    return controls.filter(
      (c) => c.id.toLowerCase().includes(q) ||
        c.title.toLowerCase().includes(q) ||
        c.family_title.toLowerCase().includes(q) ||
        c.short.toLowerCase().includes(q)
    );
  }, [controls, controlSearch]);

  const answeredCount = useMemo(
    () => Object.values(answers).filter((a) => a?.verdict).length,
    [answers]
  );

  // A Partial or Wrong verdict without a suggested control is incomplete — the
  // server rejects it, and the error table the paper needs cannot be built
  // without it. Surfaced in the row tint so nothing is silently half-done.
  const incompleteCount = useMemo(
    () => Object.values(answers).filter(
      (a) => (a?.verdict === 'p' || a?.verdict === 'w') && !(a?.suggested || '').trim()
    ).length,
    [answers]
  );

  const save = useCallback(async (queueId, next, { quiet = false } = {}) => {
    if (!next.verdict) return false;
    if (!quiet) setSavingId(queueId);
    try {
      const res = await fetch(`${API}/annotation/answer`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          queue_id: queueId,
          verdict: next.verdict,
          suggested: next.suggested || '',
          unsure: !!next.unsure,
        }),
      });
      if (res.status === 401) {
        // Keep the verdict in `unsaved` (the catch below does that) so it
        // survives in the table and can be re-saved after signing in.
        setSessionExpired(true);
        throw new Error('Session expired — sign in again, then press Save.');
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Save failed (HTTP ${res.status})`);
      }
      setUnsaved((prev) => {
        if (!prev.has(queueId)) return prev;
        const nextSet = new Set(prev);
        nextSet.delete(queueId);
        return nextSet;
      });
      setLastSaved(new Date());
      return true;
    } catch (e) {
      // The row stays in `unsaved`, so the Save button can retry it.
      setUnsaved((prev) => new Set(prev).add(queueId));
      if (!quiet) setToast({ severity: 'error', msg: e.message });
      return false;
    } finally {
      if (!quiet) setSavingId(null);
    }
  }, []);

  // Flush everything not yet confirmed by the server. Every change already
  // autosaves; this is the explicit "my work is safe" action, and the retry
  // path when a save failed while the annotator was mid-queue.
  const saveAll = useCallback(async () => {
    // Only what the server has not confirmed. Re-POSTing every answered row
    // meant one click cost a round trip per annotation — over a minute on a
    // full queue — and the button sat on "Saving…" throughout, which read as a
    // hang. `unsaved` is exactly the retry set the button label advertises:
    // update() adds a row, save() removes it once the server confirms.
    const pending = Array.from(unsaved)
      .map((queueId) => [queueId, answers[queueId]])
      .filter(([, a]) => a?.verdict);

    if (!pending.length) {
      setToast({ severity: 'success', msg: 'Everything is saved' });
      return;
    }

    setSavingAll(true);
    try {
      let ok = 0;
      let failed = 0;
      // Bounded concurrency rather than one-at-a-time: a full retry stays
      // responsive without opening a connection per annotation.
      const LANES = 6;
      const queue = [...pending];
      await Promise.all(
        Array.from({ length: Math.min(LANES, queue.length) }, async () => {
          for (;;) {
            const entry = queue.shift();
            if (!entry) return;
            if (await save(entry[0], entry[1], { quiet: true })) ok += 1;
            else failed += 1;
          }
        })
      );
      setToast(failed
        ? { severity: 'error', msg: `${ok} saved, ${failed} failed — try again` }
        : { severity: 'success', msg: `${ok} annotation${ok === 1 ? '' : 's'} saved` });
    } finally {
      setSavingAll(false);
    }
  }, [answers, unsaved, save]);

  const update = (queueId, patch, { persist = true } = {}) => {
    // Computed outside the state updater: React invokes updaters twice in
    // StrictMode, which would fire the save twice.
    const next = { ...(answers[queueId] || {}), ...patch };
    setAnswers((prev) => ({ ...prev, [queueId]: next }));
    if (next.verdict) setUnsaved((prev) => new Set(prev).add(queueId));
    if (persist) save(queueId, next);
  };

  const vendorOptions = useMemo(
    () => ['all', ...Array.from(new Set(items.map((i) => i.vendor))).sort()],
    [items]
  );
  const severityOptions = useMemo(
    () => ['all', ...Array.from(new Set(items.map((i) => i.severity))).filter(Boolean)],
    [items]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      const a = answers[it.queue_id];
      if (statusFilter === 'todo' && a?.verdict) return false;
      if (statusFilter === 'done' && !a?.verdict) return false;
      if (vendorFilter !== 'all' && it.vendor !== vendorFilter) return false;
      if (severityFilter !== 'all' && it.severity !== severityFilter) return false;
      if (q) {
        const hay = `${it.rule} ${it.issue} ${it.assigned_nist} ${it.assigned_nist_details}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, answers, statusFilter, vendorFilter, severityFilter, search]);

  // Both exports reflect the filters rather than always dumping all 300, and
  // both buttons carry the row count, so what leaves the page is never a
  // surprise. One row builder feeds both so the columns cannot drift apart.
  const buildRows = useCallback(() => {
    const header = [
      '#', 'Queue ID', 'Vendor', 'Severity', 'Rule', 'Issue', 'Recommendation',
      'Assigned control', 'Assigned title', 'Verdict', 'Correct control',
      'Correct title', 'Unsure',
    ];
    const body = filtered.map((it, n) => {
      const a = answers[it.queue_id] || {};
      const sug = controlById.get(a.suggested) || null;
      return [
        n + 1, it.queue_id, it.vendor, it.severity, it.rule, it.issue,
        it.recommendation, it.assigned_nist, it.assigned_nist_details,
        a.verdict ? VERDICT_LABEL[a.verdict] : '',
        a.suggested || '', sug ? sug.title : '',
        a.unsure ? 'yes' : '',
      ];
    });
    return { header, body };
  }, [filtered, answers, controlById]);

  // Tab-separated for the clipboard, because that pastes into Excel and Sheets
  // without an import dialog. Free text is flattened: TSV has no quoting, so a
  // stray tab or newline would shift every following column.
  const copyRows = useCallback(async () => {
    const cell = (v) => String(v ?? '').replace(/[\t\r\n]+/g, ' ').trim();
    const { header, body } = buildRows();
    const tsv = [header, ...body].map((r) => r.map(cell).join('\t')).join('\n');

    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard api unavailable');
      await navigator.clipboard.writeText(tsv);
      setCopied(true);
    } catch {
      // navigator.clipboard is absent on insecure origins and can be refused
      // outright, so fall back to a selection copy before giving up.
      try {
        const ta = document.createElement('textarea');
        ta.value = tsv;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '-1000px';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('copy rejected');
        setCopied(true);
      } catch {
        setToast({ severity: 'error', msg: 'Could not copy — the browser blocked clipboard access' });
      }
    }
  }, [buildRows]);

  // CSV keeps the text intact instead of flattening it: RFC 4180 quoting lets a
  // field carry commas, quotes and newlines. The UTF-8 BOM is for Excel, which
  // otherwise reads the file as the local codepage and mangles any non-ASCII.
  const downloadCsv = useCallback(() => {
    const cell = (v) => {
      const t = String(v ?? '');
      return /[",\r\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    const { header, body } = buildRows();
    const csv = [header, ...body].map((r) => r.map(cell).join(',')).join('\r\n');

    const who = (annotator?.username || 'annotations').replace(/[^a-z0-9_-]/gi, '');
    const name = `nscat-annotations-${who}-${body.length}rows.csv`;

    const url = URL.createObjectURL(
      new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Freeing it immediately can abort the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setToast({ severity: 'success', msg: `Downloaded ${body.length} row${body.length === 1 ? '' : 's'} as CSV` });
  }, [buildRows, annotator]);

  // The clipboard gives no visible sign it changed, so the button confirms it
  // and reverts on its own.
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (sessionExpired) {
    return (
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => authService.logout()}>
              Sign in again
            </Button>
          }
        >
          Your session expired. Verdicts you already saved are safe — sign in
          again to carry on where you left off.
        </Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          NIST SP 800-53 Mapping Annotation
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {annotator?.username}
          </Typography>
          {/* Dormant once the server has confirmed everything: every edit
              already autosaves, so an enabled Save with nothing behind it
              invites a click that does nothing. It wakes up only when a row is
              genuinely pending — a fresh edit, or one whose save failed. The
              tooltip always says why it is in the state it is in, since a
              disabled control that does not explain itself reads as broken. */}
          <Tooltip
            title={
              savingAll
                ? 'Writing pending verdicts to the server'
                : unsaved.size
                  ? `${unsaved.size} verdict${unsaved.size === 1 ? '' : 's'} not yet on the server — click to retry`
                  : lastSaved
                    ? 'Every verdict is saved'
                    : 'Nothing to save yet — verdicts save themselves as you go'
            }
          >
            {/* span: a disabled button emits no events for the tooltip. */}
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={
                  savingAll
                    ? <CircularProgress size={16} color="inherit" />
                    : unsaved.size
                      ? <SaveIcon />
                      : <CheckCircleIcon />
                }
                onClick={saveAll}
                disabled={savingAll || unsaved.size === 0}
                aria-label={
                  unsaved.size
                    ? `Save ${unsaved.size} unsaved annotation${unsaved.size === 1 ? '' : 's'}`
                    : 'All annotations saved, nothing to save'
                }
                // Held steady so waking and settling does not resize the
                // toolbar under the pointer.
                sx={{ textTransform: 'none', minWidth: 124 }}
              >
                {savingAll ? 'Saving…' : unsaved.size ? `Save (${unsaved.size})` : 'Saved'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              filtered.length === items.length
                ? `Copy all ${items.length} rows as TSV — paste into Excel or Sheets`
                : `Copy the ${filtered.length} row${filtered.length === 1 ? '' : 's'} matching the current filters, as TSV`
            }
          >
            {/* span: a disabled button fires no events, so the tooltip needs a
                wrapper that still does. */}
            <span>
              <Button
                variant="outlined"
                size="small"
                color={copied ? 'success' : 'primary'}
                startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                onClick={copyRows}
                disabled={!filtered.length}
                aria-label={`Copy ${filtered.length} annotations to the clipboard as tab-separated values`}
                // Fixed width so confirming the copy does not resize the button
                // and shift the toolbar.
                sx={{ textTransform: 'none', minWidth: 116 }}
              >
                {copied ? 'Copied' : `Copy ${filtered.length}`}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              filtered.length === items.length
                ? `Download all ${items.length} rows as a CSV file`
                : `Download the ${filtered.length} row${filtered.length === 1 ? '' : 's'} matching the current filters, as CSV`
            }
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={downloadCsv}
                disabled={!filtered.length}
                aria-label={`Download ${filtered.length} annotations as a CSV file`}
                sx={{ textTransform: 'none' }}
              >
                CSV
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<HelpOutlineIcon />}
            onClick={() => setShowRubric((s) => !s)}
            sx={{ textTransform: 'none' }}
          >
            Rubric
          </Button>
          <Tooltip title="Sign out">
            <IconButton size="small" onClick={() => authService.logout()}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Progress */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 160 }}>
            {answeredCount} of {items.length} answered
          </Typography>
          <LinearProgress
            variant="determinate"
            value={items.length ? (answeredCount / items.length) * 100 : 0}
            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
          />
          {incompleteCount > 0 && (
            <Chip
              size="small"
              color="warning"
              label={`${incompleteCount} need a suggested control`}
              onClick={() => { setStatusFilter('done'); }}
            />
          )}
          {answeredCount === items.length && incompleteCount === 0 && (
            <Chip size="small" color="success" icon={<CheckCircleIcon />} label="complete" />
          )}
          <Typography variant="caption" sx={{ color: unsaved.size ? '#c62828' : '#000', minWidth: 150, textAlign: 'right' }}>
            {unsaved.size
              ? `${unsaved.size} unsaved`
              : lastSaved
                ? 'saved'
                : 'all work saved'}
          </Typography>
        </Stack>
      </Paper>

      {/* Rubric */}
      <Collapse in={showRubric}>
        <Paper variant="outlined" sx={{
          p: 2, mb: 2, bgcolor: '#fffdf5',
          '& .MuiTypography-root': { color: '#000' },
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            What you are judging
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Whether the <strong>assigned NIST control</strong> is the right control for the finding.
            You are <em>not</em> judging whether the finding is a real problem, whether its wording is
            accurate, or whether the severity is right. Any control ID appearing inside the issue text
            is model output — ignore it and judge only the assigned control.
          </Typography>
          {/* Rubric and guidelines aligned on 7 Oct 2026 with the ones the two annotators confirmed they
              applied (Paper 1, Section 7.4). The on-page text shown from 16 Sep 2026 differed from them.
              Display text only; annotation data, verdict codes and the API are unchanged. */}
          <Typography variant="body2"><strong style={{ color: '#2e7d32' }}>Correct</strong> — the assigned control best matches the finding text.</Typography>
          <Typography variant="body2"><strong style={{ color: '#ed6c02' }}>Partial</strong> — the assigned control lies in the same family or an adjacent security domain, but the sub-control is off.</Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}><strong style={{ color: '#c62828' }}>Wrong</strong> — the assigned control lies in a different security domain entirely.</Typography>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Controls of overlapping scope:</strong> distinct base controls addressing related
            concerns (for example AC-4 and SC-7) are different mappings, not partial credit.
          </Typography>
          <Typography variant="body2">
            <strong>Enhancements:</strong> an assignment naming the correct base control but an
            inapplicable enhancement (for example SI-4(15) where SI-4 applies) is <strong>Partial</strong>.
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<ListAltIcon />}
            onClick={() => setShowControlList(true)}
            disabled={!controls.length}
            sx={{ textTransform: 'none' }}
          >
            {controls.length
              ? `NIST SP 800-53 Rev 5 — all ${controls.length} controls`
              : 'Loading control list…'}
          </Button>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#000' }}>
            The same catalogue the Correct-control picker draws from. Base controls and enhancements,
            grouped by family.
          </Typography>
        </Paper>
      </Collapse>

      {/* Full control catalogue */}
      <Dialog open={showControlList} onClose={() => setShowControlList(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#000' }}>
          NIST SP 800-53 Rev 5 — {controls.length} controls and enhancements
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Filter by control ID, title, family or text…"
            value={controlSearch}
            onChange={(e) => setControlSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
            inputProps={{ style: { color: '#000' } }}
            sx={{ mb: 1.5 }}
          />
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#000' }}>
            Showing {listedControls.length} of {controls.length}.
          </Typography>
          {/* Plain DOM rather than MUI components: 1,014 entries means ~3,000
              styled components, which stalls the dialog for a second on open. */}
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {listedControls.map((c, i) => {
              const newFamily = i === 0 || listedControls[i - 1].family !== c.family;
              return (
                <React.Fragment key={c.id}>
                  {newFamily && (
                    <li style={{
                      color: '#000', fontWeight: 700, fontSize: '0.8125rem',
                      lineHeight: 2.4, listStyle: 'none',
                    }}>
                      {c.family} — {c.family_title}
                    </li>
                  )}
                  <li style={{
                    paddingLeft: c.is_enhancement ? 24 : 0,
                    paddingTop: 3, paddingBottom: 3, listStyle: 'none',
                  }}>
                    <span style={{
                      display: 'block', fontFamily: 'monospace', fontWeight: 700,
                      fontSize: '0.8125rem', color: '#000',
                    }}>
                      {c.id} — {c.title}
                    </span>
                    <span style={{ display: 'block', fontSize: '0.7rem', color: '#000' }}>
                      {c.short}
                    </span>
                  </li>
                </React.Fragment>
              );
            })}
            {listedControls.length === 0 && (
              <Typography variant="body2" sx={{ color: '#000', py: 2 }}>
                Nothing matches “{controlSearch}”.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowControlList(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            size="small"
            value={statusFilter}
            exclusive
            onChange={(_, v) => v && setStatusFilter(v)}
          >
            <ToggleButton value="all">All ({items.length})</ToggleButton>
            <ToggleButton value="todo">To do ({items.length - answeredCount})</ToggleButton>
            <ToggleButton value="done">Answered ({answeredCount})</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            select
            label="Vendor"
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ minWidth: 180 }}
          >
            {vendorOptions.map((v) => (
              <option key={v} value={v}>{v === 'all' ? 'All vendors' : (VENDOR_LABEL[v] || v)}</option>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Severity"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ minWidth: 140 }}
          >
            {severityOptions.map((s) => (
              <option key={s} value={s}>{s === 'all' ? 'All severities' : s}</option>
            ))}
          </TextField>
          <TextField
            size="small"
            placeholder="Search rule, issue, control..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 250 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Showing {filtered.length} of {items.length}
          </Typography>
        </Stack>
      </Paper>

      {/* Findings table */}
      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }}>#</TableCell>
                <TableCell sx={{ width: 130 }}>Verdict</TableCell>
                <TableCell sx={{ width: 230 }}>Assigned control</TableCell>
                <TableCell sx={{ width: 200 }}>Correct control</TableCell>
                <TableCell sx={{ width: 110 }}>Vendor</TableCell>
                <TableCell sx={{ width: 80 }}>Severity</TableCell>
                <TableCell>Rule / Issue / Recommendation</TableCell>
                <TableCell sx={{ width: 60 }} align="center">
                  <Tooltip title="Unsure — send to adjudication"><span>?</span></Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((it, n) => {
                const a = answers[it.queue_id] || {};
                const needsSuggested = (a.verdict === 'p' || a.verdict === 'w');
                const incomplete = needsSuggested && !(a.suggested || '').trim();
                const suggestedControl = controlById.get(a.suggested) || null;
                return (
                  <TableRow
                    key={it.queue_id}
                    hover
                    sx={{
                      verticalAlign: 'top',
                      backgroundColor: incomplete
                        ? 'rgba(255, 152, 0, 0.10)'
                        : a.verdict
                          ? 'rgba(76, 175, 80, 0.07)'
                          : 'inherit',
                    }}
                  >
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{n + 1}</Typography>
                    </TableCell>

                    <TableCell>
                      <TextField
                        size="small"
                        select
                        SelectProps={{ native: true }}
                        value={a.verdict || ''}
                        onChange={(e) => update(it.queue_id, { verdict: e.target.value || null })}
                        sx={{ minWidth: 115 }}
                      >
                        <option value="">—</option>
                        <option value="c">Correct</option>
                        <option value="p">Partial</option>
                        <option value="w">Wrong</option>
                      </TextField>
                      {a.verdict && (
                        <Chip
                          size="small"
                          color={VERDICT_COLOR[a.verdict]}
                          label={VERDICT_LABEL[a.verdict]}
                          sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
                        />
                      )}
                      {savingId === it.queue_id && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          saving…
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#000' }}>
                        {it.assigned_nist}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: '#000' }}>
                        {it.assigned_nist_details}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {needsSuggested ? (
                        <>
                        <Autocomplete
                          size="small"
                          options={controls}
                          filterOptions={controlFilter}
                          value={controlById.get(a.suggested) || null}
                          // A selection is a discrete act, so it saves at once
                          // rather than waiting for blur the way typing did.
                          onChange={(_, v) => update(it.queue_id, { suggested: v ? v.id : '' })}
                          getOptionLabel={(o) => (typeof o === 'string' ? o : o.id)}
                          isOptionEqualToValue={(o, v) => o.id === v.id}
                          slotProps={{ paper: { sx: { width: 520 } } }}
                          renderOption={(props, o) => (
                            <li {...props} key={o.id} style={{ ...props.style, display: 'block', padding: '4px 12px' }}>
                              <span style={{
                                display: 'block', fontFamily: 'monospace', fontWeight: 700,
                                fontSize: '0.8125rem', color: '#000',
                              }}>
                                {o.id} — {o.title}
                              </span>
                              <span style={{ display: 'block', fontSize: '0.7rem', color: '#000' }}>
                                {o.family_title} · {o.short}
                              </span>
                            </li>
                          )}
                          ListboxProps={{ style: { maxHeight: 360 } }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="search…"
                              error={incomplete}
                              helperText={incomplete ? 'required' : ' '}
                              inputProps={{ ...params.inputProps, style: { fontFamily: 'monospace', color: '#000' } }}
                            />
                          )}
                          sx={{ width: 185 }}
                        />
                        {/* Mirrors the assigned-control cell: once a control is
                            chosen the picker input shows only its id, so print
                            the title and description underneath. The annotator
                            can then check the choice without reopening the
                            list, and compare it against the assigned control
                            sitting in the previous column. */}
                        {suggestedControl && (
                          <Box sx={{ mt: 0.5, width: 185 }}>
                            <Typography variant="caption" sx={{ display: 'block', color: '#000' }}>
                              {suggestedControl.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block', color: 'text.secondary',
                                fontSize: '0.65rem', lineHeight: 1.35, mt: 0.25,
                              }}
                            >
                              {suggestedControl.short}
                            </Typography>
                          </Box>
                        )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{VENDOR_LABEL[it.vendor] || it.vendor}</Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={it.severity}
                        sx={{
                          bgcolor: SEVERITY_COLOUR[it.severity] || '#757575',
                          color: 'white',
                          height: 20,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#000', mb: 0.5 }}
                      >
                        {it.rule || '—'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>{it.issue}</Typography>
                      {it.recommendation && (
                        <Typography variant="caption" color="text.secondary">
                          {it.recommendation}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Checkbox
                        size="small"
                        checked={!!a.unsure}
                        onChange={(e) => update(it.queue_id, { unsure: e.target.checked })}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
        Answers save automatically. You can close this page and return at any time.
      </Typography>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity || 'info'} onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
